/**
 * Performance Chart Component
 *
 * Displays a line chart showing fund value performance over time with:
 * - Fund NAV/value trajectory
 * - Benchmark comparison (e.g., Nifty 50)
 * - Invested amount reference line
 * - Time period selector (1M, 3M, 6M, 1Y)
 * - Interactive hover/touch to show values at specific points
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useState, useCallback } from "react";

import { CHART_CONFIG } from "./constants";
import { calculateValueRange } from "./utils";
import type { PerformanceChartProps, TimePeriod } from "./types";

// Import subcomponents
import { PeriodSelector } from "./components/PeriodSelector";
import { YAxis } from "./components/YAxis";
import { XAxis } from "./components/XAxis";
import { InvestedLine } from "./components/InvestedLine";
import { FundLine } from "./components/FundLine";
import { BenchmarkLine } from "./components/BenchmarkLine";
import { TooltipOverlay } from "./components/TooltipOverlay";
import { ChartLegend } from "./components/ChartLegend";
import { SummaryStats } from "./components/SummaryStats";
import { EmptyState } from "./components/EmptyState";

/**
 * Performance Chart
 *
 * Displays a line chart showing fund value performance over time,
 * compared against a benchmark with the invested amount as reference.
 *
 * @example
 * ```tsx
 * <PerformanceChart
 *   data={[
 *     { date: "2024-07-01", fundValue: 100000, benchmarkValue: 100000 },
 *     { date: "2024-08-01", fundValue: 105000, benchmarkValue: 103000 },
 *     { date: "2024-09-01", fundValue: 108000, benchmarkValue: 105000 },
 *     // ... more data points
 *   ]}
 *   investedAmount={100000}
 *   currentValue={115000}
 *   benchmarkName="Nifty 50"
 *   fundName="HDFC Mid-Cap Fund"
 * />
 * ```
 */
export function PerformanceChart({
  data,
  investedAmount,
  currentValue,
  benchmarkName = "Nifty 50",
  fundName = "Your Fund",
  defaultPeriod = "6M",
  title = "Performance",
  className,
  onPeriodChange,
}: PerformanceChartProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(defaultPeriod);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handlePeriodChange = useCallback(
    (period: TimePeriod) => {
      setSelectedPeriod(period);
      setActiveIndex(null);
      onPeriodChange?.(period);
    },
    [onPeriodChange]
  );

  const hasData = data.length >= 2;
  const { min, max } = calculateValueRange(data, investedAmount);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <PeriodSelector
            selected={selectedPeriod}
            onChange={handlePeriodChange}
          />
        </div>
      </CardHeader>
      <CardContent>
        {hasData ? (
          <div className="w-full">
            {/* Summary Stats */}
            <SummaryStats
              investedAmount={investedAmount}
              currentValue={currentValue}
            />

            {/* Chart SVG */}
            <svg
              viewBox={`0 0 ${CHART_CONFIG.width} ${CHART_CONFIG.height}`}
              className="w-full h-auto"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`Performance chart showing fund value over ${selectedPeriod}`}
            >
              <title>Performance Chart</title>
              <desc>
                A line chart showing fund performance compared to {benchmarkName}{" "}
                over the selected time period.
              </desc>

              {/* Axes */}
              <YAxis min={min} max={max} />
              <XAxis data={data} />

              {/* Invested amount reference line */}
              <InvestedLine
                investedAmount={investedAmount}
                min={min}
                max={max}
              />

              {/* Benchmark line */}
              <BenchmarkLine data={data} min={min} max={max} />

              {/* Fund value line */}
              <FundLine data={data} min={min} max={max} />

              {/* Interactive tooltip overlay */}
              <TooltipOverlay
                data={data}
                min={min}
                max={max}
                investedAmount={investedAmount}
                benchmarkName={benchmarkName}
                activeIndex={activeIndex}
                onIndexChange={setActiveIndex}
              />
            </svg>

            {/* Legend */}
            <ChartLegend benchmarkName={benchmarkName} fundName={fundName} />
          </div>
        ) : (
          <EmptyState />
        )}
      </CardContent>
    </Card>
  );
}

export default PerformanceChart;
